// TODO: remove this file if this file replaces the default RichMediaTextEditorSunrise.tsx
/* istanbul ignore file */
import type { IAllProps as EditorProps } from '@hugerte/hugerte-react';

import { colors, space } from '@change/ds-tokens';

import localeMapping from '#config/hugerte/locale-mapping.json';

import { useLocale } from '#src/app/shared/hooks/l10n';

/**
 * Hyperlinks are styled separately since they are considered content within the
 * HugeRTE editor, handled by the content_style attribute in the editor config.
 *
 * The editor iframe is an isolated document, so it cannot inherit the page's fonts
 * or CSS variables — Tailwind classes and CSS custom properties are unavailable.
 * We therefore pull values straight from the design-system token JS (link colour =
 * typography-interactive, padding = the spacing scale) and interpolate them as
 * literals. The font-family is left literal because it must match the family name
 * loaded by the Google Fonts @import above; the paragraph margins are intentionally
 * em-relative so they scale with the content font size.
 */
export const getHugeRteContentStyle = (): string => `
@import url('https://fonts.googleapis.com/css2?family=Commissioner:wght@100..900&display=swap');
	body {
		font-family: "Commissioner", ui-sans-serif, system-ui, sans-serif;
		font-weight: 400;
		margin: 0;
		padding: ${space[4]}px;
	}
	p {
		margin: 0;
	}
	p + p {
		margin-top: 1.5em;
	}
  a {
    color: ${colors.semantic.light.typography.interactive};
  }
`;

type EditorInitConfig = EditorProps['init'];

const BASE_URL = '/corgi/assets/vendors/hugerte/1.0.11';

const hugeRteStaticConfig: EditorInitConfig = {
	anchor_bottom: false,
	anchor_top: false,
	base_url: BASE_URL,
	branding: false,
	browser_spellcheck: true,
	convert_urls: false,
	file_picker_types: 'image',
	min_height: 350,
	inline_boundaries: false,
	link_assume_external_targets: 'https',
	link_context_toolbar: true,
	link_default_target: '_blank',
	link_target_list: false,
	link_title: false,
	menubar: false,
	oninit: 'setPlainText',
	paste_as_text: true,
	paste_remove_spans: true,
	paste_remove_styles: true,
	plugins: 'link image lists help',
	relative_urls: false,
	skin: 'hugerte-5',
	statusbar: false,
	toolbar: 'bold italic | numlist bullist | link | help',
	promotion: false,
	// disabled for now as the "paste" command doesn't work due to browser security (see https://github.com/hugerte/hugerte/issues/7254)
	// the "link" command remains in the toolbar anyway
	contextmenu: false,
	// https://github.com/hugerte/hugerte/security/advisories/GHSA-5359-pvf2-pw78
	convert_unsafe_embeds: true,
	// https://github.com/hugerte/hugerte/security/advisories/GHSA-438c-3975-5x3f
	sandbox_iframes: true,
};

export const useDefaultConfig = (): EditorInitConfig => {
	const locale = useLocale();
	const localeCode = localeMapping.localeMapping[locale];

	return {
		...hugeRteStaticConfig,
		content_style: getHugeRteContentStyle(),
		language: localeCode,
		language_url: `${BASE_URL}/langs/${localeCode}.js`,
	};
};
